import { Route, Service, Schedule, Stop, StopsData } from '../types/ferry';
import { SeasonalService } from './seasonalService';
import { logger } from '../utils/logger';

export interface RouteResult {
  route: Route;
  service: Service;
  schedule: Schedule;
  fromStop: Stop;
  toStop: Stop;
  direction: string;
}

export class RouteSearchService {
  private routes: Route[];
  private stops: StopsData;

  constructor(routes: Route[], stops: StopsData) {
    this.routes = routes;
    this.stops = stops;
  }

  /**
   * Search for direct routes between two stops
   */
  searchDirectRoutes(
    fromStopId: string,
    toStopId: string,
    date: Date,
    time: string
  ): RouteResult[] {
    const results: RouteResult[] = [];
    const fromStop = this.stops[fromStopId];
    const toStop = this.stops[toStopId];

    if (!fromStop || !toStop) {
      return results;
    }

    // Find routes that connect these two stops
    for (const route of this.routes) {
      const fromIndex = route.stops.indexOf(fromStopId);
      const toIndex = route.stops.indexOf(toStopId);

      // Check if both stops are on this route and in the right order
      if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
        // Check if route is operating on the given date
        if (!SeasonalService.isDateInSeason(date, route.seasonalOperation)) {
          continue;
        }

        // Get the available direction keys from the first service
        const availableDirections = Object.keys(route.services[0]?.schedules || {});
        
        // Find the correct direction key by checking which direction contains our from->to pattern
        let direction = '';
        
        for (const dir of availableDirections) {
          // Parse the direction key to extract the stops (e.g., "takamatsu_to_naoshima" -> ["takamatsu", "naoshima"])
          const dirParts = dir.split('_to_');
          if (dirParts.length === 2) {
            const dirFromStop = dirParts[0];
            const dirToStop = dirParts[1];
            
            // Check if this direction matches our desired travel direction
            // Handle both full stop IDs and shortened forms
            const fromMatches = dirFromStop === fromStopId || 
                               (fromStopId === 'naoshima_miyanoura' && dirFromStop === 'naoshima');
            const toMatches = dirToStop === toStopId || 
                             (toStopId === 'naoshima_miyanoura' && dirToStop === 'naoshima');
            
            if (fromMatches && toMatches) {
              direction = dir;
              break;
            }
          }
        }

        logger.log(`Route ${route.id}: ${fromStopId} -> ${toStopId}, selected direction=${direction}, available directions:`, availableDirections);

        // Skip if no valid direction found
        if (!direction) {
          logger.log(`No valid direction found for route ${route.id}`);
          continue;
        }

        // Check each service on this route
        for (const service of route.services) {
          const schedules = service.schedules[direction];
          if (!schedules) {
            logger.log(`No schedules found for direction ${direction} in service ${service.id}`);
            continue;
          }

          // Filter schedules based on time and day
          const validSchedules = this.filterSchedulesByTimeAndDay(schedules, date, time);

          for (const schedule of validSchedules) {
            // Calculate actual duration from departure and arrival times
            const actualDuration = this.calculateDuration(schedule.departure, schedule.arrival);
            
            // Create a service object with the correct duration
            const serviceWithCorrectDuration = {
              ...service,
              duration: actualDuration
            };
            
            results.push({
              route,
              service: serviceWithCorrectDuration,
              schedule,
              fromStop,
              toStop,
              direction
            });
          }
        }
      }
    }

    // Sort by departure time
    return results.sort((a, b) => a.schedule.departure.localeCompare(b.schedule.departure));
  }

  /**
   * Filter schedules by time and day of week
   */
  private filterSchedulesByTimeAndDay(
    schedules: Schedule[],
    date: Date,
    time: string
  ): Schedule[] {
    const dayOfWeek = this.getDayOfWeek(date);
    
    return schedules.filter(schedule => {
      // Check if service operates on this day
      if (!this.isDayValid(schedule.days, dayOfWeek, date)) {
        return false;
      }

      // Check special conditions
      if (schedule.special) {
        if (schedule.special === 'teshima_museum_open' && dayOfWeek === 'tue') {
          return false; // Museum is closed on Tuesdays
        }
        if (schedule.special === 'teshima_museum_closed' && dayOfWeek !== 'tue') {
          return false; // Only operates on Tuesdays when museum is closed
        }
        if (schedule.special === 'summer_obon_aug12_15') {
          // Operates only on Saturdays, Sundays, Mondays, holidays, and August 12-15
          const month = date.getMonth() + 1; // getMonth() returns 0-11
          const day = date.getDate();
          const isAugust12_15 = month === 8 && day >= 12 && day <= 15;
          const isWeekendMonday = dayOfWeek === 'sat' || dayOfWeek === 'sun' || dayOfWeek === 'mon';
          // Note: Holiday detection would need a proper holiday calendar
          if (!isWeekendMonday && !isAugust12_15) {
            return false;
          }
        }
        if (schedule.special === 'non_weekend_non_holidays_except_aug12_15') {
          // Operates on Tue, Wed, Thu, Fri except during August 12-15
          const month = date.getMonth() + 1;
          const day = date.getDate();
          const isAugust12_15 = month === 8 && day >= 12 && day <= 15;
          const isTargetDays = dayOfWeek === 'tue' || dayOfWeek === 'wed' || dayOfWeek === 'thu' || dayOfWeek === 'fri';
          if (!isTargetDays || isAugust12_15) {
            return false;
          }
        }
        if (schedule.special === 'hazardous_materials_first_tuesday') {
          // Show service but with warning (handled in UI)
          // On first Tuesday: restricted to hazmat vehicles only
        }
        if (schedule.special === 'summer_megijima_aug1_20') {
          // Only operates during August 1-20, 2025
          const month = date.getMonth() + 1; // getMonth() returns 0-11
          const day = date.getDate();
          const year = date.getFullYear();
          if (year !== 2025 || month !== 8 || day < 1 || day > 20) {
            return false;
          }
        }
        if (schedule.special === 'setouchi_triennale_2025') {
          // Only operates during Setouchi Triennale 2025 periods AND on weekends/holidays
          const month = date.getMonth() + 1; // getMonth() returns 0-11
          const day = date.getDate();
          const year = date.getFullYear();
          const dateString = `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
          
          // Check if date is within any Triennale period
          const triennalePeriods = [
            { start: '04-18', end: '05-25' },
            { start: '08-01', end: '08-31' },
            { start: '10-03', end: '11-09' }
          ];
          
          const isInTriennalePeriod = year === 2025 && triennalePeriods.some(period => 
            dateString >= period.start && dateString <= period.end
          );
          
          // Must be both in Triennale period AND on weekends/holidays
          const isWeekendHoliday = dayOfWeek === 'sat' || dayOfWeek === 'sun'; // Note: Holiday detection would need proper calendar
          
          if (!isInTriennalePeriod || !isWeekendHoliday) {
            return false;
          }
        }
        if (schedule.special === 'festival_weekend_except_apr29') {
          const month = date.getMonth() + 1;
          const day = date.getDate();
          if (month === 4 && day === 29) {
            return false; // Special festival service skips April 29th
          }
        }
      }

      // Check if departure time is after requested time
      return schedule.departure >= time;
    });
  }

  /**
   * Check if a schedule operates on the given day
   */
  private isDayValid(scheduleDays: string[], dayOfWeek: string, date: Date): boolean {
    // Handle "daily" schedules
    if (scheduleDays.includes('daily')) {
      return true;
    }

    // Handle specific days
    if (scheduleDays.includes(dayOfWeek)) {
      return true;
    }

    // Handle weekends
    if (scheduleDays.includes('weekends') && (dayOfWeek === 'sat' || dayOfWeek === 'sun')) {
      return true;
    }

    // Handle holidays (simplified - in real app would need holiday calendar)
    if (scheduleDays.includes('holidays')) {
      // For now, treat Saturdays and Sundays as holidays
      return dayOfWeek === 'sat' || dayOfWeek === 'sun';
    }

    return false;
  }

  /**
   * Get day of week abbreviation
   */
  private getDayOfWeek(date: Date): string {
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    return days[date.getDay()];
  }

  /**
   * Calculate duration in minutes from departure and arrival times
   */
  private calculateDuration(departureTime: string, arrivalTime: string): number {
    const [depHours, depMinutes] = departureTime.split(':').map(Number);
    const [arrHours, arrMinutes] = arrivalTime.split(':').map(Number);
    
    const depTotalMinutes = depHours * 60 + depMinutes;
    const arrTotalMinutes = arrHours * 60 + arrMinutes;
    
    // Handle crossing midnight (arrival next day)
    if (arrTotalMinutes < depTotalMinutes) {
      return (arrTotalMinutes + 24 * 60) - depTotalMinutes;
    }
    
    return arrTotalMinutes - depTotalMinutes;
  }

  /**
   * Get all possible stops for route planning
   */
  getAllStops(): StopsData {
    return this.stops;
  }

  /**
   * Check if two stops are directly connected
   */
  areStopsDirectlyConnected(fromStopId: string, toStopId: string): boolean {
    for (const route of this.routes) {
      const fromIndex = route.stops.indexOf(fromStopId);
      const toIndex = route.stops.indexOf(toStopId);
      
      if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get the first Tuesday of the month for a given date
   */
  private getFirstTuesdayOfMonth(date: Date): number {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // Start from the 1st of the month
    for (let day = 1; day <= 7; day++) {
      const testDate = new Date(year, month, day);
      if (testDate.getDay() === 2) { // Tuesday is day 2
        return day;
      }
    }
    return 1; // Fallback, should never happen
  }

  /**
   * Get seasonal warnings for a specific date and route combination
   */
  getSeasonalWarnings(fromStopId: string, toStopId: string, date: Date): string[] {
    const warnings: string[] = [];
    const dayOfWeek = this.getDayOfWeek(date);
    
    for (const route of this.routes) {
      const fromIndex = route.stops.indexOf(fromStopId);
      const toIndex = route.stops.indexOf(toStopId);
      
      if (fromIndex !== -1 && toIndex !== -1) {
        // Keep existing seasonal operation warnings
        if (route.seasonalOperation) {
          if (!SeasonalService.isDateInSeason(date, route.seasonalOperation)) {
            const nextPeriod = SeasonalService.getNextSeasonalPeriod(route.seasonalOperation);
            if (nextPeriod) {
              warnings.push(
                `${route.seasonalOperation.name}: Service resumes ${nextPeriod.start}`
              );
            } else {
              warnings.push(
                `${route.seasonalOperation.name}: Service not operating on selected date`
              );
            }
          }
        }

        // Add museum closure warning for the Teshima Ferry routes
        if ((route.id === 'takamatsu_teshima_ieura_direct' || route.id === 'takamatsu_honmura_ieura_via' || route.id === 'takamatsu_teshima_karato') && dayOfWeek === 'tue') {
          warnings.push('Limited services due to museum closure');
        }

        if (route.id === 'shodoshima_tonosho_inujima') {
          const festivalWeekendWarning = 'Operates only on Fri, Sat, Sun and holidays during Triennale.';
          if (!warnings.includes(festivalWeekendWarning)) {
            warnings.push(festivalWeekendWarning);
          }
        }

        if (route.id === 'kyobashi_inujima_teshima') {
          const kyobashiWarning = 'Operates Sat, Sun, and festival holidays only (no service on Apr 29).';
          if (!warnings.includes(kyobashiWarning)) {
            warnings.push(kyobashiWarning);
          }
        }
      }
    }

    return warnings;
  }
}
